import lightGallery from 'lightgallery';
import { LightGallery } from 'lightgallery/lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TKMasonry from '@tk/utilities/tk.masonry';

import 'lightgallery/scss/lightgallery.scss';
import 'lightgallery/scss/lg-thumbnail.scss';
import 'lightgallery/scss/lg-zoom.scss';

import './tk.light.gallery.scss';

export default class TKLightGallery extends TKCustomElementFactory {
    private galleryInstance: LightGallery | null = null;
    private masonryInstance: TKMasonry | null = null;
    private isRenderReady = false;

    constructor() {
        super();
        this.classList.remove('tk-light-gallery--ready');
    }

    connectedCallback(): void {
        this.hideUntilReady();
        this.initMasonry();
        this.initGallery();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.masonryInstance) {
            this.masonryInstance.destroy();
            this.masonryInstance = null;
        }
        if (this.galleryInstance) {
            this.galleryInstance.destroy();
            this.galleryInstance = null;
        }
    }

    private initMasonry(): void {
        const gap = this.getNumberAttribute('masonry-gap', 10);
        const rowHeight = this.getNumberAttribute('masonry-row-height', 240);
        const lastRowAttr = this.getConfigAttribute('masonry-last-row', 'nojustify').toLowerCase();
        const waitForImages = this.getBooleanAttribute('masonry-wait-for-images', true);
        let lastRowMode: 'justify' | 'nojustify' | 'hide';

        if (lastRowAttr === 'hide') {
            lastRowMode = 'hide';
        } else if (lastRowAttr === 'justify') {
            lastRowMode = 'justify';
        } else {
            lastRowMode = 'nojustify';
        }

        this.masonryInstance = new TKMasonry(this, {
            itemSelector: '[data-tk-light-gallery-item]',
            gap: gap,
            targetRowHeight: rowHeight,
            lastRowMode: lastRowMode,
            waitForImages: waitForImages,
        });

        this.masonryInstance
            .init()
            .then(() => {
                this.dispatchEvent(new CustomEvent('masonry-ready'));
            })
            .finally(() => {
                this.showWhenReady();
            });
    }

    private getConfigAttribute(name: string, defaultValue: string): string {
        const value = this.getAttribute(name) || this.getDataAttribute(name, defaultValue);
        const trimmedValue = value.trim();
        return trimmedValue === '' ? defaultValue : trimmedValue;
    }

    private getNumberAttribute(name: string, defaultValue: number): number {
        const value = Number.parseInt(this.getConfigAttribute(name, String(defaultValue)), 10);
        return Number.isFinite(value) ? value : defaultValue;
    }

    private getBooleanAttribute(name: string, defaultValue: boolean): boolean {
        const value = this.getConfigAttribute(name, defaultValue ? 'true' : 'false').toLowerCase();

        if (value === 'true' || value === '1' || value === 'yes') {
            return true;
        }

        if (value === 'false' || value === '0' || value === 'no') {
            return false;
        }

        return defaultValue;
    }

    private hideUntilReady(): void {
        this.isRenderReady = false;
        this.classList.remove('tk-light-gallery--ready');
    }

    private showWhenReady(): void {
        if (!this.isConnected || this.isRenderReady) {
            return;
        }

        this.isRenderReady = true;
        requestAnimationFrame(() => {
            if (this.isConnected) {
                this.classList.add('tk-light-gallery--ready');
            }
        });
    }

    private initGallery(): void {
        const speed = parseInt(this.getDataAttribute('speed', '500'), 10);

        this.galleryInstance = lightGallery(this, {
            plugins: [lgZoom, lgThumbnail],
            speed: speed,
            licenseKey: process.env.LIGHTGALLERY_LICENSE_KEY || '',
            selector: '[data-tk-light-gallery-item]',
            thumbnail: true,
            zoom: true,
        });
    }
}
